import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  clearAdminSession,
  getAdminProfile,
} from "../../features/adminAuth/adminAuthStorage";

const navItems = [
  {
    label: "Dashboard",
    path: "/admin/dashboard",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="1" width="6" height="6" rx="1.5" />
        <rect x="9" y="1" width="6" height="6" rx="1.5" />
        <rect x="1" y="9" width="6" height="6" rx="1.5" />
        <rect x="9" y="9" width="6" height="6" rx="1.5" />
      </svg>
    ),
  },
  {
    label: "Users",
    path: "/admin/users",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="8" cy="5" r="3" />
        <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      </svg>
    ),
  },
  {
    label: "Inventory",
    path: "/admin/inventory",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 5.5l-6 3-6-3" />
        <path d="M8 8.5V15" />
        <path d="M14 5.5v6l-6 3-6-3v-6l6-3 6 3z" />
      </svg>
    ),
  },
  {
    label: "MTN Bundles",
    path: "/admin/mtn-bundles",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1.5" y="3" width="13" height="10" rx="2" />
        <path d="M4 6.5h8M4 9.5h5" />
      </svg>
    ),
  },
  {
    label: "Transactions",
    path: "/admin/transactions",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 5h10M10 2l3 3-3 3" />
        <path d="M13 11H3M6 8l-3 3 3 3" />
      </svg>
    ),
  },
  {
    label: "Orders",
    path: "/admin/orders",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <rect x="2" y="2" width="12" height="12" rx="2" />
        <path d="M5 6h6M5 9h6M5 12h3" />
      </svg>
    ),
  },
  {
    label: "Emails",
    path: "/admin/emails",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1.5" y="3" width="13" height="10" rx="2" />
        <path d="M2 4l6 4 6-4" />
      </svg>
    ),
  },
  {
    label: "Blog",
    path: "/admin/blog",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 2.5l3 3-7 7H3v-3l7-7z" />
      </svg>
    ),
  },
  {
    label: "Legal",
    path: "/admin/legal",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 1l5 2.5v4c0 3.5-2.5 6-5 7-2.5-1-5-3.5-5-7V3.5L8 1z" />
      </svg>
    ),
  },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = getAdminProfile();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    clearAdminSession();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="admin-shell">
      <button
        type="button"
        className="admin-mobile-menu-button"
        aria-label="Open admin navigation menu"
        onClick={() => setIsSidebarOpen(true)}
      >
        <span />
        <span />
        <span />
      </button>

      <div
        className={isSidebarOpen ? "admin-sidebar-overlay admin-sidebar-overlay-open" : "admin-sidebar-overlay"}
        onClick={() => setIsSidebarOpen(false)}
        aria-hidden="true"
      />

      <aside className={isSidebarOpen ? "admin-sidebar admin-sidebar-open" : "admin-sidebar"}>
        <div className="admin-brand">
          <span className="admin-brand-mark" />
          <div className="admin-brand-text">
            <h1>Verixxon</h1>
            <p>{profile?.email || "admin"}</p>
          </div>
          <button
            type="button"
            className="admin-mobile-close-button"
            aria-label="Close admin navigation menu"
            onClick={() => setIsSidebarOpen(false)}
          >
            ✕
          </button>
        </div>

        <div className="admin-nav-divider" />

        <nav className="admin-nav" aria-label="Admin navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                isActive ? "admin-nav-link admin-nav-link-active" : "admin-nav-link"
              }
              onClick={() => setIsSidebarOpen(false)}
            >
              <span className="admin-nav-icon">{item.icon}</span>
              <span className="admin-nav-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="admin-nav-divider" />

        <button type="button" className="logout-btn" onClick={handleLogout}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: "7px", verticalAlign: "middle" }}>
            <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3M11 11l4-4-4-4M15 8H6" />
          </svg>
          Sign Out
        </button>
      </aside>

      <main className="admin-main">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
